import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Layout from '../../../components/Layout';
import {
  LockClosedIcon,
  CheckCircleIcon,
  DocumentTextIcon,
  DownloadIcon,
  ExclamationCircleIcon,
  SearchIcon,
} from '@heroicons/react/outline';
import { ChevronRightIcon } from '@heroicons/react/solid';
import Breadcrumb from '../../../components/Breadcrumb';
import { PatientDataContext } from '../../../context/PatientDataContext';
import { Tooltip } from 'react-tooltip';
// import { TriggerFetchLatestDocForDownload } from '../../../utils/TriggerFetchLatestDocForDownload';
import AdditionalDocuments from './components/AdditionalDocuments';
import PreAssessmentDocuments from './components/PreAssessmentDocuments';
import RatingScalesWizard from './components/RatingScalesWizard';
import { auth } from '../../../firebase';
import { isAdhdAssessmentFlow, getPadStaleness } from '../../../utils/conditionChecks';
import PadRedoDialog from '../../../components/PadRedoDialog';
import useWorkflowConfig from '../../../hooks/useWorkflowConfig';
import { ASSESSMENT_CONCLUSION_SECTION_TITLE } from '../../../utils/assessmentEnd';
import { getRequiredCoreSections, isSectionRequired } from '../../../utils/requiredSectionsUtils';
import { TriggerCoverPage } from '../../../utils/TriggerCoverPage';
import { db } from '../../../firebase';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { getStaleProcessingResets } from '../../../utils/processingStatusHelpers';
import {
  buildBusinessFieldsFingerprint,
  buildDashboardProcessingStatuses,
  computeDebouncedStatuses,
} from '../../../utils/snapshotRefreshHelpers';
import { useAuth } from '../../../hooks/useAuth';
import { formatDateTime } from '../../../utils/timestampUtils';
import ReportBundleDialog from './components/ReportBundleDialog';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { TriggerRenderDocTemplate } from '../../../utils/TriggerRenderDocTemplate';
import { TriggerRegenerateCIStage } from '../../../utils/TriggerRegenerateCIStage';
import { TriggerRegenerateSSIStage } from '../../../utils/TriggerRegenerateSSIStage';
import { buildCIExclusionsContext, getVisibleCISections } from '../../../utils/clinicalInterviewWorkflow';
import {
  isClinicianAssignedToPatient,
  getEffectiveSSIClinician,
} from '../../../utils/clinicianPermissions';
import { DocumentEmbedDialog } from '../8_diagnosticDecision/components/DocumentEmbedDialog';
import {
  filterSectionsForBundleDialog,
  getCarSectionProcessingKeys,
  getManageDocumentsPanelCopy,
  getVisibleSectionDisplayNumbers,
  shouldIncludeCoverPageSection,
} from './documentsDashboardHelpers';

/**
 * Maps every previewable section ID to its Firestore document metadata key
 * under `patient.documents`.  Used to look up `lastGenerated` for the
 * embed URL cache key so that the preview always serves the latest
 * rendered version of each section.
 *
 * Note: coverPageReport and ratingScalesReport are camelCase (a historical
 * naming inconsistency).  The backend fetchLatestDocumentForEmbed CF now
 * resolves these correctly via its _EMBED_STAGE_TO_DOC_KEY lookup table, so
 * all sections can be previewed.
 */
const SECTION_DOCUMENT_KEY = {
  coverPage:               'coverPageReport',
  clinicalInterview:       'clinicalinterviewReport',
  semiStructuredInterview: 'semistructuredinterviewReport',
  diagnosticDecision:      'diagnosticdecisionReport',
  postAssessmentDiscussion:'postassessmentdiscussionReport',
  attachmentsRatingScales: 'ratingScalesReport',
  qbCheck:                 'qbcheckReport',
  qbTest:                  'qbtestReport',
};

export default function DocumentsDashboard() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { patientDataModel, fetchPatientData, flushPendingUpdates } = useContext(PatientDataContext);

   /**
   * Processing Status Management
   * --------------------------
   * We track the state of document generation for all document types in a single object.
   * Each document can be in one of three states:
   * - 'idle': Document is not currently being processed
   * - 'processing': Document is actively being generated
   * - 'error': Document generation failed
   * - 'complete': Document was successfully generated (transient state, normally becomes 'idle')
   * 
   * This state-based approach allows us to:
   * 1. Show appropriate UI feedback during long-running operations
   * 2. Disable UI actions during processing
   * 3. Handle errors gracefully
   */
   const [processingStatuses, setProcessingStatuses] = useState({
    clinicalInterview: 'idle',
    semiStructuredInterview: 'idle',
    diagnosticDecision: 'idle',
    postAssessmentDiscussion: 'idle',
    qbCheck: 'idle',
    qbTest: 'idle',
    ratingScales: 'idle',
    coverPage: 'idle',
    patientReport: 'idle',
    bupaLetter: 'idle',
  });

  // Ref that mirrors processingStatuses so the onSnapshot callback can
  // compare previous vs current statuses for transition detection without
  // including processingStatuses in the useEffect dependency array (which
  // would cause an infinite re-subscription loop because setProcessingStatuses
  // always creates a new object reference).
  const prevStatusesRef = useRef(processingStatuses);

  // Fingerprint of business-critical manageReports fields from the previous
  // onSnapshot.  Used to detect changes that should trigger a full context
  // refresh (fetchPatientData) — e.g. ssiResetRequired toggling, approval
  // flags changing — beyond just processing-status transitions.
  const prevBusinessFieldsRef = useRef('');

  // Per-key timers for the processing-status display debounce.  When a key
  // transitions OUT of 'processing', we delay updating the UI for 500ms so
  // that auto-helper retry cycles don't cause a green flash.
  const statusDebounceTimersRef = useRef({});

  // Trailing debounce timer for fetchPatientData calls originating from the
  // onSnapshot listener.  Coalesces rapid successive events (e.g. a CF
  // writing status + metadata in two separate Firestore operations) into a
  // single fetch.
  const fetchDebounceTimerRef = useRef(null);

  // Fetch initial patient data
  useEffect(() => {
    // Force fresh fetch with forceRefresh=true to ensure latest data
    fetchPatientData(patientId, true);
  }, [patientId, fetchPatientData]);

  /**
   * Firestore Real-time Status Monitoring
   * ------------------------------------
   * This effect sets up a Firestore listener to monitor changes to document
   * processing statuses AND business-critical manageReports fields.
   *
   * Design decisions:
   *
   * 1. onSnapshot for real-time updates: Document generation can take 30-40+ s,
   *    so we need immediate UI feedback when statuses change.
   *
   * 2. Fingerprint-based refresh: Instead of the narrow "processing → complete"
   *    heuristic, we hash a set of business-critical fields (generated, approved,
   *    resetRequired).  Any change to those fields triggers a full context
   *    refresh so the Dashboard row status derivation stays current.
   *
   * 3. Debounced fetchPatientData: A 300ms trailing debounce coalesces rapid
   *    successive snapshot events (e.g. a Cloud Function writing status and
   *    metadata in two separate Firestore operations) into a single fetch,
   *    preventing concurrent request races in PatientDataContext.
   *
   * 4. Debounced processing display: When a status leaves 'processing', the UI
   *    waits 500ms before updating the display.  If the auto-helper retry sets
   *    the status back to 'processing' within that window, the user never sees
   *    the intermediate green flash — they see a smooth amber-to-amber transition.
   *
   * 5. Transition detection via ref: We use prevStatusesRef instead of including
   *    processingStatuses in the dependency array to avoid an infinite
   *    re-subscription loop.
   *
   * 6. Error handling: We handle 'deadline-exceeded' errors gracefully — they are
   *    expected for long-running Firestore connections and non-fatal.
   */
  useEffect(() => {
    // Capture ref values at effect creation time so the cleanup function
    // uses the same object even if the ref is reassigned before unmount.
    const debounceTimers = statusDebounceTimersRef.current;

    const unsubscribe = onSnapshot(
      doc(db, 'patients', patientId),
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const manageReports = data?.dataModel?.['6_manageReports'] || {};

          // --- 1. Build display processing statuses (complete → idle for UI) ---
          // Self-healing below uses raw manageReports, not this normalized map.
          // The documents map is passed so the helper can overlay pdfStatus
          // (e.g. show "error" when the section completed but PDF creation failed).
          const rawStatuses = buildDashboardProcessingStatuses(manageReports, data?.documents);

          // --- 2. Determine whether a context refresh is needed ---
          // Two triggers:
          //   a) Business fields fingerprint changed (approval, generated, reset flags)
          //   b) Any processing status left 'processing' (report generation finished)
          const newFingerprint = buildBusinessFieldsFingerprint(manageReports);
          const fingerprintChanged = newFingerprint !== prevBusinessFieldsRef.current;

          const prev = prevStatusesRef.current;
          const anyLeftProcessing = Object.keys(rawStatuses).some(
            (key) => prev[key] === 'processing' && rawStatuses[key] !== 'processing'
          );

          if (fingerprintChanged || anyLeftProcessing) {
            // Trailing debounce: coalesce rapid successive events into one fetch
            if (fetchDebounceTimerRef.current) clearTimeout(fetchDebounceTimerRef.current);
            fetchDebounceTimerRef.current = setTimeout(() => {
              fetchDebounceTimerRef.current = null;
              fetchPatientData(patientId, true);
            }, 300);
          }

          // --- 3. Apply debounced processing-status display ---
          // Transitions OUT of 'processing' are delayed so auto-helper retries
          // don't cause a brief green flash in the UI.
          const immediateStatuses = computeDebouncedStatuses(
            prev,
            rawStatuses,
            statusDebounceTimersRef.current,
            // Deferred callback: apply the real value once the debounce window
            // passes without the status re-entering 'processing'.
            (key, value) => {
              setProcessingStatuses((current) => ({ ...current, [key]: value }));
            },
            500
          );

          // --- 4. Update refs and state ---
          prevBusinessFieldsRef.current = newFingerprint;
          prevStatusesRef.current = rawStatuses;
          setProcessingStatuses(immediateStatuses);

          // --- 5. Self-healing: reset processing statuses stuck > 10 min ---
          const resets = getStaleProcessingResets(manageReports);
          if (Object.keys(resets).length > 0) {
            console.warn('Auto-resetting stale processing statuses:', resets);
            updateDoc(doc(db, 'patients', patientId), resets).catch((e) =>
              console.error('Failed to auto-reset stale processing:', e)
            );
          }
        }
      },
      (error) => {
        console.error('Error in Firestore snapshot listener:', error);
        if (error.code === 'deadline-exceeded') {
          console.log('Deadline exceeded in listener - this is expected for long-running operations');
        }
      }
    );

    // Clean up all timers and the listener on unmount.
    // Uses the locally captured `debounceTimers` to satisfy the
    // react-hooks/exhaustive-deps rule (ref.current may change by cleanup).
    return () => {
      unsubscribe();
      if (fetchDebounceTimerRef.current) clearTimeout(fetchDebounceTimerRef.current);
      Object.values(debounceTimers).forEach(clearTimeout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, fetchPatientData]);

  // Get claims from useAuth hook - no token refresh needed, always fresh
  const { claims } = useAuth();

  // Derive workflow config flags (e.g. isSSIAlwaysExpected, replaceCARwithReportBuilder)
  // from the centralised workflow config rather than hard-coding org-name checks.
  const wf = useWorkflowConfig(patientDataModel, claims);

  /**
   * UI Helper Function: Check Processing Status
   * -----------------------------------------
   * This function checks if any document is currently being processed.
   * 
   * Why use useCallback:
   * 1. Performance: This function is used in multiple places in the UI and in other effects
   * 2. Stability: Ensures we don't create a new function instance on every render
   * 
   * Usage:
   * - Disable the "Download Report" button when any document is processing
   * - Show processing indicators in the UI
   * - Prevent certain user actions during processing
   */
  // Only check core CAR section statuses — the Patient Report and BUPA Letter
  // are additional documents that should NOT block the CAR download button.
  // Cover Page is excluded for Report Builder workflows (TOC + SLaM) so a
  // stale/in-flight Cover Page job cannot disable Download after the row is gone.
  const isAnyCARSectionProcessing = useCallback(() => {
    const carKeys = getCarSectionProcessingKeys(wf.replaceCARwithReportBuilder);
    return carKeys.some((key) => processingStatuses[key] === 'processing');
  }, [processingStatuses, wf.replaceCARwithReportBuilder]);

  // Block CAR download while the Patient Report CF chain is running to
  // prevent Firestore write contention between generateClinicalReportBundle
  // and the PR CFs (generatePatientReport / renderPatientReportTemplate).
  const isPatientReportProcessing = processingStatuses?.patientReport === 'processing';

  /**
   * Helper Function for Locked Section Text
   * --------------------------------------
   * Returns appropriate explanatory text for locked sections.
   * Only shows text when section.status === 'locked' (grey state).
   */
  const getLockedSectionText = useCallback((sectionId) => {
    switch (sectionId) {
      case 'coverPage':
        return "Available after all other report sections are approved";
      
      case 'clinicalInterview':
        return "Available after Clinical Interview assessment module is completed";
      
      case 'semiStructuredInterview':
        return "Available after Semi-Structured Interview assessment module is completed";
      
      case 'diagnosticDecision':
        return "Available after Diagnostic Decision assessment module is completed";
      
      case 'postAssessmentDiscussion':
        // The Post-Assessment Discussion report only takes its final positive/
        // negative shape once the formal Diagnostic Decision is recorded (the
        // report structure follows the DD), so the report row unlocks off the DD
        // rather than the PAD module itself.
        return "Available after the Diagnostic Decision module is completed";
      
      default:
        return null;
    }
  }, []);

  // Add state for QbCheck visibility
  const [showQbCheck, setShowQbCheck] = useState(false);
  // Add state for QbTest visibility
  const [showQbTest, setShowQbTest] = useState(false);
  // Add state for transition effect
  const [showQbTransition, setShowQbTransition] = useState(false);
  // Add state for Pre-Assessment Documents visibility (ADHD and Autism Associates only)
  const [showPreAssessmentDocuments, setShowPreAssessmentDocuments] = useState(false);

  // Determine QbCheck/QbTest visibility from the patient's workflow config.
  // The config resolution chain handles legacy patients (no stamped config)
  // and patient-level overrides (workflowIncludesQbTest) internally.
  useEffect(() => {
    if (!claims?.organisation || !patientDataModel) {
      return;
    }

    setShowQbCheck(wf.isQbCheckEnabled);
    setShowQbTest(wf.isQbTestEnabled);

    setTimeout(() => {
      setShowQbTransition(true);
    }, 100);
  }, [claims?.organisation, patientDataModel, wf.isQbCheckEnabled, wf.isQbTestEnabled]);

  // Pre-Assessment Documents: visible when the patient's workflow config has
  // preAssessmentReport enabled (currently only A&A).
  useEffect(() => {
    if (!claims?.organisation) {
      setShowPreAssessmentDocuments(false);
      return;
    }
    
    setShowPreAssessmentDocuments(wf.isPreAssessmentReportEnabled);
  }, [claims?.organisation, wf.isPreAssessmentReportEnabled]);

  /**
   * Diagnosis-Path Section Visibility
   * ----------------------------------
   * Which core sections are required (and therefore visible) is determined by
   * the shared utility requiredSectionsUtils.getRequiredCoreSections().
   * Business rules encoded there:
   *   ADHD flow (any org):               CI, SSI, DD, PAD
   *   Not-ADHD + SSI-always (SLaM etc.): CI, SSI, DD, PAD (shortened PAD)
   *   Not-ADHD + standard orgs:          CI, DD only
   *
   * NOTE: isAdhdFlow (derived from isAdhdAssessmentFlow above) is deliberately
   * NOT used here for section visibility. It is only used for QbCheck
   * visibility and SSI/PAD transition animations.
   */
  const requiredSections = useMemo(
    () => getRequiredCoreSections(patientDataModel, claims?.organisation),
    [patientDataModel, claims?.organisation]
  );

  const isSectionHiddenByDiagnosisPath = useCallback((sectionId) => {
    return !isSectionRequired(sectionId, requiredSections);
  }, [requiredSections]);

  // Workflow-aware ADHD flow determination: uses PPD for hierarchical DF orgs
  // and DD outcome for flat DF orgs.  Replaces the old `isAdhdAfterCI` boolean
  // which always used PPD (incorrectly returning true for flat DF patients
  // where PPD is absent).
  const isAdhdFlow = useMemo(
    () => isAdhdAssessmentFlow(patientDataModel, wf),
    [patientDataModel, wf]
  );

  // Controls the slide-in animation for SSI/PAD rows when they first appear.
  // Only relevant for non-SSI-always orgs where SSI/PAD visibility is dynamic.
  const [showADHDTransition, setShowADHDTransition] = useState(false);

  // SSI/PAD slide-in animation timer.  For SSI-always orgs the rows are
  // always visible, so we set the transition flag immediately (the animation
  // class won't be applied anyway due to the !wf.isSSIAlwaysExpected guard).
  // For standard orgs we delay 100ms so the CSS transition plays.
  useEffect(() => {
    if (wf.isSSIAlwaysExpected) {
      setShowADHDTransition(true);
      return;
    }
    if (patientDataModel?.dataModel && !showADHDTransition) {
      const timer = setTimeout(() => setShowADHDTransition(true), 100);
      return () => clearTimeout(timer);
    }
  }, [patientDataModel?.dataModel, wf.isSSIAlwaysExpected, showADHDTransition]);

  const ASSIGNED_CLINICIANS = useMemo(() =>
    patientDataModel?.clinicians || {},
    [patientDataModel]);

  const MANAGE_REPORTS = useMemo(() =>
    patientDataModel?.dataModel?.['6_manageReports'] || {},
    [patientDataModel]);

  /**
   * Get message for SSI needs-redo status (3 Borough / SLaM workflow)
   * This message is shown when the Lead clinician has triggered an SSI reset
   */
  const getSSINeedsRedoMessage = useCallback(() => {
    const resetBy = MANAGE_REPORTS?.ssiResetRequestedByName || 'Lead clinician';
    return `${resetBy} has requested changes to the Semi-Structured Interview section based on their Clinical Interview findings.`
  }, [MANAGE_REPORTS]);

  /**
   * Access rule: who can generate shared attachments (Rating Scales)?
   * ---------------------------------------------------------------
   * Historically, we treated Rating Scales as "owned" by the SSI clinician (because the section
   * displayed SSI as the reviewer). That unintentionally blocked other clinicians assigned to the
   * patient from generating this section.
   *
   * Going forward, Rating Scales generation is a *team-level* action: any *primary* clinician
   * assigned to the patient (CI/SSI/DD/PAD/Lead) should be able to generate/re-generate it.
   *
   * Important: DD supporting clinicians should NOT be able to generate Rating Scales (they may be
   * shown on the DD row, but are not considered "assigned" for Rating Scales generation).
   *
   * Note: this is UI-gating only; the backend callable is also tightened to enforce assignment.
   */
  const canGenerateRatingScales = useMemo(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return false;
    return isClinicianAssignedToPatient(patientDataModel, uid);
  }, [patientDataModel]);

  /**
   * UI helper: list all distinct PRIMARY clinicians assigned to this patient
   * ---------------------------------------------------------------
   * Most report rows show a single "Assigned to" badge because there's a single stage owner.
   * Rating Scales is a shared attachment where multiple primary clinicians may legitimately take
   * action, so we show multiple badges (deduped) to reflect reality.
   *
   * Includes ONLY:
   * - Primary CI / SSI / DD / PAD / Lead clinicians (deduped)
   *
   * Output:
   * - [{ id, name, tooltip }]
   */
  const primaryAssignedClinicianBadges = useMemo(() => {
    const clinicians = patientDataModel?.clinicians || {};

    // clinicianId -> { id, name, stageLabels: Set<string> }
    const byId = new Map();
    const ensure = (id, name) => {
      if (!id) return null;
      if (!byId.has(id)) byId.set(id, { id, name: name || 'Unknown', stageLabels: new Set() });
      return byId.get(id);
    };

    const stageDefs = [
      { key: 'clinicianCI', label: 'Clinical Interview' },
      { key: 'clinicianSSI', label: 'Semi-Structured Interview' },
      { key: 'clinicianDD', label: 'Diagnostic Decision' },
      { key: 'clinicianPAD', label: 'Post-Assessment Discussion' },
      { key: 'clinicianLead', label: 'Lead Clinician' },
    ];

    stageDefs.forEach(({ key, label }) => {
      const entry = clinicians?.[key];
      const rec = ensure(entry?.clinicianId, entry?.clinicianName);
      if (rec) rec.stageLabels.add(label);
    });

    return Array.from(byId.values()).map((rec) => ({
      id: rec.id,
      name: rec.name,
      tooltip: `Assigned stages: ${Array.from(rec.stageLabels).join(', ')}`,
    }));
  }, [patientDataModel]);

  /**
   * UI helper: Diagnostic Decision badges (primary + supporting)
   * ----------------------------------------------------------
   * For the DD row specifically, we *do* want to display supporting clinicians alongside the
   * primary DD clinician (because they are meaningfully involved in that stage).
   *
   * Output:
   * - [{ id, name, tooltip }]
   */
  const diagnosticDecisionClinicianBadges = useMemo(() => {
    const dd = patientDataModel?.clinicians?.clinicianDD;
    if (!dd) return [];

    const byId = new Map();
    const add = (id, name, label) => {
      if (!id) return;
      if (!byId.has(id)) byId.set(id, { id, name: name || 'Unknown', labels: new Set() });
      byId.get(id).labels.add(label);
    };

    // Primary DD clinician
    add(dd.clinicianId, dd.clinicianName, 'Diagnostic Decision (primary)');

    // Supporting clinicians (display only)
    if (Array.isArray(dd.supportingClinicianIds) && dd.supportingClinicianIds.length > 0) {
      dd.supportingClinicianIds.forEach((id, idx) => {
        const name = Array.isArray(dd.supportingClinicianNames) ? dd.supportingClinicianNames[idx] : undefined;
        add(id, name || 'MDT Review Clinician', 'Diagnostic Decision (MDT Review)');
      });
    }

    return Array.from(byId.values()).map((rec) => ({
      id: rec.id,
      name: rec.name,
      tooltip: `DD role: ${Array.from(rec.labels).join(', ')}`,
    }));
  }, [patientDataModel]);

  // Helper functions for styling
  const getBackgroundClass = (docId, docStatus, processingStatus) => {
    // Map special section IDs to their processing status keys
    let processingStatusKey;
    if (docId === 'attachmentsRatingScales') {
      processingStatusKey = 'ratingScales';
    } else if (docId === 'coverPage') {
      processingStatusKey = 'coverPage';
    } else {
      processingStatusKey = docId;
    }
    // Get the processing status for this specific document
    const docProcessingStatus = processingStatus[processingStatusKey] || 'idle';

    // Show processing, pending, or error status
    if (docProcessingStatus === 'processing' || docProcessingStatus === 'pending') {
      return 'bg-amber-50 hover:bg-amber-100';
    }
    if (docProcessingStatus === 'error') {
      return 'bg-red-50 hover:bg-red-100';
    }

    // Default statuses
    if (docStatus === 'approved') {
      return 'bg-green-50 hover:bg-green-100';
    }
    if (docStatus === 'ready-for-review') {
      return 'bg-violet-50 hover:bg-violet-100';
    }
    // SSI needs re-editing (3 Borough / SLaM workflow - Lead clinician triggered reset)
    if (docStatus === 'needs-redo') {
      return 'bg-amber-50 hover:bg-amber-100';
    }

    return 'bg-gray-50 hover:bg-gray-100';
  };

  const getCircleClass = (docId, docStatus, processingStatus) => {
    // Map special section IDs to their processing status keys
    let processingStatusKey;
    if (docId === 'attachmentsRatingScales') {
      processingStatusKey = 'ratingScales';
    } else if (docId === 'coverPage') {
      processingStatusKey = 'coverPage';
    } else {
      processingStatusKey = docId;
    }
    // Get the processing status for this specific document
    const docProcessingStatus = processingStatus[processingStatusKey] || 'idle';

    // Show processing, pending, or error status
    if (docProcessingStatus === 'processing' || docProcessingStatus === 'pending') {
      return 'bg-amber-50 border-amber-200 text-amber-700';
    }
    if (docProcessingStatus === 'error') {
      return 'bg-red-50 border-red-200 text-red-700';
    }

    // Default statuses
    if (docStatus === 'approved') {
      return 'bg-green-50 border-green-200 text-green-700';
    }
    if (docStatus === 'ready-for-review') {
      return 'bg-violet-50 border-violet-200 text-violet-700';
    }
    // SSI needs re-editing (3 Borough / SLaM workflow - Lead clinician triggered reset)
    if (docStatus === 'needs-redo') {
      return 'bg-amber-50 border-amber-200 text-amber-700';
    }

    return 'bg-gray-50 border-gray-200 text-gray-700';
  };

  // Wrap getStatus in useCallback to maintain reference stability
  const getStatus = useCallback((reportType) => {
    const isGenerated = MANAGE_REPORTS[`${reportType}ReportGenerated`];
    const isApproved = MANAGE_REPORTS[`${reportType}ReportApproved`];

    // console.log('reportType:', reportType, 'isGenerated:', isGenerated, 'isApproved:', isApproved);

    // Special case for SSI: Check if reset is required (3 Borough / SLaM workflow)
    // When ssiResetRequired is true, the SSI section needs re-editing by the Lead clinician
    if (reportType === 'ssi' && MANAGE_REPORTS.ssiResetRequired === true) {
      return 'needs-redo';
    }

    if (isApproved) {
      return 'approved';
    } else if (isGenerated) {
      return 'ready-for-review';
    } else {
      return 'locked';
    }
  }, [MANAGE_REPORTS]); // Add MANAGE_REPORTS as dependency

  const getDocumentSections = useMemo(() => {
    if (!ASSIGNED_CLINICIANS) return [];
    else if (!MANAGE_REPORTS) return [];

    // When a Lead SSI override is active, the Lead becomes the SSI reviewer
    // (and rating-scales reviewer). Report attribution still uses the original
    // clinicianSSI slot — only review/edit ownership transfers here.
    const effectiveSSI = getEffectiveSSIClinician(patientDataModel);
    const ssiReviewerName =
      effectiveSSI.clinicianName ||
      ASSIGNED_CLINICIANS.clinicianSSI?.clinicianName ||
      'Unassigned';
    const ssiReviewerId =
      effectiveSSI.clinicianId ||
      ASSIGNED_CLINICIANS.clinicianSSI?.clinicianId ||
      'Unassigned';

    const sections = [
      // TOC and SLaM Report Builder omit the standalone Cover Page — clinicians
      // approve CI / SSI / DD / PAD (and attachments) as individual sections.
      ...(shouldIncludeCoverPageSection(wf.replaceCARwithReportBuilder) ? [{
        id: 'coverPage',
        reportStage: 'Cover Page',
        order: 1,
        status: getStatus('cp'),
        containsAIOutputs: false,
        embedReportStage: 'Cover Page',
        reviewer: {
          name: ASSIGNED_CLINICIANS.clinicianCI?.clinicianName || 'Unassigned',
          id: ASSIGNED_CLINICIANS.clinicianCI?.clinicianId || 'Unassigned',
        },
      }] : []),
      {
        id: 'clinicalInterview',
        reportStage: 'Clinical Interview',
        embedReportStage: 'Clinical Interview',
        order: 2,
        status: getStatus('ci'),
        containsAIOutputs: true,
        reviewer: {
          name: ASSIGNED_CLINICIANS.clinicianCI?.clinicianName || 'Unassigned',
          id: ASSIGNED_CLINICIANS.clinicianCI?.clinicianId || 'Unassigned',
        },
        route: 'review-document-ci',
      },
      {
        id: 'semiStructuredInterview',
        reportStage: 'Semi-Structured Interview',
        embedReportStage: 'Semi-Structured Interview',
        order: 3,
        status: getStatus('ssi'),
        containsAIOutputs: true,
        reviewer: {
          name: ssiReviewerName,
          id: ssiReviewerId,
        },
        route: 'review-document-ssi',
      },
      {
        id: 'diagnosticDecision',
        reportStage: 'Diagnostic Decision',
        embedReportStage: 'Diagnostic Decision',
        order: 4,
        status: getStatus('dd'),
        containsAIOutputs: true,
        reviewer: {
          name: ASSIGNED_CLINICIANS.clinicianDD?.clinicianName || 'Unassigned',
          id: ASSIGNED_CLINICIANS.clinicianDD?.clinicianId || 'Unassigned',
        },
        route: 'review-document-dd',
      },
      {
        id: 'postAssessmentDiscussion',
        reportStage: 'Post-Assessment Discussion',
        embedReportStage: 'Post-Assessment Discussion',
        order: 5,
        status: getStatus('pad'),
        containsAIOutputs: true,
        reviewer: {
          name: ASSIGNED_CLINICIANS.clinicianPAD?.clinicianName || 'Unassigned',
          id: ASSIGNED_CLINICIANS.clinicianPAD?.clinicianId || 'Unassigned',
        },
        route: 'review-document-pad',
      },
      {
        id: 'attachmentsRatingScales',
        reportStage: 'Attachments - Rating Scales',
        order: 6,
        status: getStatus('ratingScales'),
        containsAIOutputs: false,
        embedReportStage: 'Attachments - Rating Scales',
        reviewer: {
          name: ssiReviewerName,
          id: ssiReviewerId,
        },
        route: 'review-document-rating-scales',
      },
    ];

    // Add QbCheck section when the org uses QbCheck and the patient is on an
    // ADHD assessment path.  For hierarchical DF this checks the PPD; for flat
    // DF it checks the DD outcome — so QbCheck only appears after DD confirms
    // ADHD for flat DF orgs, rather than always defaulting to visible.
    //
    // Exception: passthrough mode (TOC) shows QbCheck regardless of diagnosis
    // status, since clinicians need to enter scores at any point in the workflow.
    const isPassthroughQbCheck = wf.qbCheckMode === 'passthrough';
    if (showQbCheck && (isAdhdFlow || isPassthroughQbCheck)) {
      const isPassthroughMode = isPassthroughQbCheck;
      sections.push({
        id: 'qbCheck',
        reportStage: 'Attachments - QbCheck Results',
        embedReportStage: 'QbCheck',
        order: sections.length + 1,
        status: getStatus('qbCheck'),
        containsAIOutputs: !isPassthroughMode,
        isPassthroughMode,
        reviewer: {
          name: ASSIGNED_CLINICIANS.clinicianSSI?.clinicianName || 'Unassigned',
          id: ASSIGNED_CLINICIANS.clinicianSSI?.clinicianId || 'Unassigned',
        },
        route: isPassthroughMode ? null : 'review-document-qb-check',
      });
    }

    // QbTest section — visible when wf.isQbTestEnabled resolves to true
    // (config-driven via supplementaryDocuments, with workflowIncludesQbTest patient-level override for legacy WLC patients)
    if (showQbTest) {
      sections.push({
        id: 'qbTest',
        reportStage: 'Attachments - QbTest Results',
        embedReportStage: 'QbTest',
        order: sections.length + 1,
        status: getStatus('qbTest'),
        containsAIOutputs: true,
        reviewer: {
          name: ASSIGNED_CLINICIANS.clinicianSSI?.clinicianName || 'Unassigned',
          id: ASSIGNED_CLINICIANS.clinicianSSI?.clinicianId || 'Unassigned',
        },
        route: 'review-document-qb-test',
      });
    }

    return sections;
  }, [
    ASSIGNED_CLINICIANS,
    MANAGE_REPORTS,
    getStatus,
    showQbCheck,
    showQbTest,
    isAdhdFlow,
    wf.qbCheckMode,
    wf.replaceCARwithReportBuilder,
    patientDataModel,
  ]);

  const sections = getDocumentSections;

  // Circle numbers follow the rows actually on screen. Hidden rows (early
  // exit drops SSI, DD and PAD) are left out of the count, so Rating Scales
  // becomes 2 rather than keeping its full-assessment position.
  const sectionDisplayNumbers = useMemo(
    () => getVisibleSectionDisplayNumbers(sections, isSectionHiddenByDiagnosisPath),
    [sections, isSectionHiddenByDiagnosisPath]
  );

  // Panel chrome: Report Builder + Download Sections for TOC/SLaM omit workflows;
  // Clinical Assessment Report + Download Report for standard orgs. TOC still
  // hides the download button entirely (showDownloadButton === false).
  const panelCopy = getManageDocumentsPanelCopy({
    replaceCARwithReportBuilder: wf.replaceCARwithReportBuilder,
    isTOCWorkflow: wf.isTOCWorkflow,
  });

  // Updated getStatusIcon function to include processing indicators
  const getStatusIcon = (section) => {
    const docId = section.id;
    const docStatus = section.status;
    
    // Priority check: SSI needs-redo status takes precedence over processing status
    // This ensures the "Changes requested" message is shown instead of "Finalising..."
    if (docStatus === 'needs-redo') {
      return (
        <div className="flex items-center gap-1 text-amber-600">
          <ExclamationCircleIcon className="h-5 w-5" />
          <span className="text-sm font-medium">Changes requested</span>
        </div>
      );
    }
    
    // Map special section IDs to their processing status keys
    let processingStatusKey;
    if (docId === 'attachmentsRatingScales') {
      processingStatusKey = 'ratingScales';
    } else if (docId === 'coverPage') {
      processingStatusKey = 'coverPage';
    } else {
      processingStatusKey = docId;
    }
    const docProcessingStatus = processingStatuses[processingStatusKey] || 'idle';

    // Show processing spinner instead of status icon when processing
    if (docProcessingStatus === 'processing') {
      return (
        <div className="flex items-center gap-2 text-amber-600">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-sm font-medium">Finalising...</span>
        </div>
      );
    }

    // Show amber "Preparing PDF" when the section completed but the PDF is
    // being retried in the background.  Preview and Download are disabled via
    // isPdfReady; the onSnapshot listener picks up the flip to ready/error.
    if (docProcessingStatus === 'pending') {
      return (
        <div className="flex items-center gap-2 text-amber-600">
          <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-sm font-medium">Preparing PDF</span>
        </div>
      );
    }

    // Show error indicator
    if (docProcessingStatus === 'error') {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <ExclamationCircleIcon className="h-5 w-5" />
          <span className="text-sm font-medium">An error occurred. Please try again.</span>
        </div>
      );
    }

    // Regular status icons based on document status
    switch (docStatus) {
      case 'locked':
        return <LockClosedIcon className="w-5 h-5 text-gray-400" />;
      case 'ready-for-review':
        return <DocumentTextIcon className="w-5 h-5 text-violet-600" />;
      case 'approved':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      default:
        return null;
    }
  };

  const [showBundleDialog, setShowBundleDialog] = useState(false);
  const [showCIRegenerateDialog, setShowCIRegenerateDialog] = useState(false);
  const [isCIRegenerating, setIsCIRegenerating] = useState(false);
  const [showSSIRegenerateDialog, setShowSSIRegenerateDialog] = useState(false);
  const [isSSIRegenerating, setIsSSIRegenerating] = useState(false);

  // SLaM PAD redo (v2) — opened from the Post-Assessment Discussion row's
  // staleness cue when the completed discussion no longer matches the finalised
  // Diagnostic Decision. Opens straight on the redo details step.
  const [showPadRedoDialog, setShowPadRedoDialog] = useState(false);

  // Two-state PAD staleness cue for the PAD document row (SLaM only). Alert until
  // the clinician decides; quieter once they knowingly leave the mismatch as is.
  const padStaleness = getPadStaleness(patientDataModel);
  const showPadStalenessCue = wf.isSLaMWorkflow && padStaleness.isStale;
  const padMismatchAcknowledged = Boolean(
    patientDataModel?.dataModel?.['5_postAssessmentDiscussion']?.padMismatchAcknowledgedAt
  );
  // Note: after a PAD redo, "View original" lives on Patient Overview / the
  // live PAD proforma (PadRedoArchiveBanner) — not on this Documents row.
  // Documents Dashboard is about AI report outputs; the archive viewer is the
  // read-only proforma snapshot, so a post-recompletion link here is misleading.
  const delay = useCallback((ms) => new Promise(resolve => setTimeout(resolve, ms)), []);

  // Preview dialog state — a single DocumentEmbedDialog instance is shared
  // across all CAR section rows AND the AdditionalDocuments panel (via the
  // onPreview callback prop).  The dialog swaps content by updating the
  // reportStage / lastGenerated state on each open.
  const [selectedPreviewReport, setSelectedPreviewReport] = useState(null);
  const [selectedPreviewLastGenerated, setSelectedPreviewLastGenerated] = useState(null);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

  const handleOpenPreview = useCallback((reportStage, lastGenerated) => {
    setSelectedPreviewReport(reportStage);
    setSelectedPreviewLastGenerated(lastGenerated || null);
    setIsPreviewDialogOpen(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setIsPreviewDialogOpen(false);
    setTimeout(() => {
      setSelectedPreviewReport(null);
      setSelectedPreviewLastGenerated(null);
    }, 300);
  }, []);

  // Note: legacy helper removed (getApprovedAndPendingSections) as the old alert was replaced by ReportBundleDialog

  const handleDownloadClick = () => {
    setShowBundleDialog(true);
  };

  // Also update hasApprovedSections to only consider visible sections
  const hasApprovedSections = useMemo(() => {
    // Filter for visible sections first
    const visibleSections = sections.filter(section => {
      if (isSectionHiddenByDiagnosisPath(section.id)) return false;

      // For Qb sections, only include if they should be shown based on organization
      if ((section.id === 'qbCheck' && !showQbCheck) ||
        (section.id === 'qbTest' && !showQbTest)) {
        return false;
      }

      return true;
    });

    // Check if any visible section is approved
    return visibleSections.some(section => section.status === 'approved');
  }, [sections, isSectionHiddenByDiagnosisPath, showQbCheck, showQbTest]);

  const hasAvailableScales = useMemo(() => {
    if (!patientDataModel?.formData) return false;

    // Check if any forms have scale results
    return Object.values(patientDataModel.formData).some(form =>
      form?.scaleResults?.scale && form.scaleResults.complete
    );
  }, [patientDataModel?.formData]);

  // Compute visible sections list for the bundle dialog (respect gating rules)
  const visibleSectionsForDialog = useMemo(() => {
    const filtered = sections.filter(section => {
      if (isSectionHiddenByDiagnosisPath(section.id)) return false;
      if ((section.id === 'qbCheck' && !showQbCheck) || (section.id === 'qbTest' && !showQbTest)) {
        return false;
      }
      return true;
    });
    // Belt-and-suspenders: drop Cover Page for Report Builder even if a stale
    // section object were present (Phase 2 SLaM / TOC omit standalone Cover Page).
    return filterSectionsForBundleDialog(filtered, wf.replaceCARwithReportBuilder);
  }, [sections, isSectionHiddenByDiagnosisPath, showQbCheck, showQbTest, wf.replaceCARwithReportBuilder]);

  const [showRatingScalesWizard, setShowRatingScalesWizard] = useState(false);

  // Add memoization to AdditionalDocuments to prevent unnecessary re-renders.
  // Processing statuses are passed as props from the Dashboard's consolidated
  // onSnapshot listener so AdditionalDocuments no longer needs its own listener.
  // ADHD flow is no longer passed as a prop — AdditionalDocuments computes it
  // internally from patientDataModel (context) and wf (hook), and re-renders
  // via useContext when patientDataModel changes.
  const MemoizedAdditionalDocuments = useMemo(() => (
    <AdditionalDocuments
      patientId={patientId}
      clinicianId={auth.currentUser?.uid}
      prProcessingStatus={processingStatuses.patientReport}
      bupaProcessingStatus={processingStatuses.bupaLetter}
      onPreview={handleOpenPreview}
    />
  ), [patientId, processingStatuses.patientReport, processingStatuses.bupaLetter, handleOpenPreview]);

  // Memoize PreAssessmentDocuments to prevent unnecessary re-renders
  // This component has its own Firestore listener for processing status,
  // so we only need to re-render when patientId or showPreAssessmentDocuments changes
  const MemoizedPreAssessmentDocuments = useMemo(() => {
    if (!showPreAssessmentDocuments) return null;
    return (
      <PreAssessmentDocuments
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
      />
    );
  }, [patientId, showPreAssessmentDocuments]);

  return (
    <Layout activePage="dashboard">
      <Breadcrumb tupleList={[["Patient Overview", `/patient/${patientId}`], ["Documents"]]} />

      {/* Patient Info Panel */}
      {patientDataModel && (
        <div className="pl-8 pr-8">
        <div className="w-full px-6 py-5 bg-gray-50 rounded-xl border border-gray-200 justify-start items-center gap-14 inline-flex text-left mb-4">
          <div className="grow shrink basis-0 h-16 justify-start items-center gap-5 flex">
            <div className="grow shrink basis-0 flex-col justify-start items-start gap-1 inline-flex">
              <div className="self-stretch text-gray-900 text-3xl font-semibold leading-9">
                {patientDataModel.firstName} {patientDataModel.lastName}
              </div>
              <div className="self-stretch text-slate-800 text-base font-normal leading-normal">
                {patientDataModel.email}
              </div>
            </div>
          </div>      
          <div className="flex justify-start items-center gap-3">
            <Link
              to={`/patient/${patientId}`}
              className="px-4 py-2 font-bold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500 disabled:opacity-50 inline-flex items-center gap-1"
            >              
              Go to Patient Overview
              <ChevronRightIcon className="w-5 h-5" />
            </Link>
          </div>
        </div>
        </div>
      )}

      <div className="flex-grow gap-4">

        {/* Pre-Assessment Documents Panel (ADHD and Autism Associates only) */}
        {MemoizedPreAssessmentDocuments}

        {/* Main Assessment Report Panel */}
        <div className="max-w-7xl p-8 mx-auto text-left bg-white rounded-xl shadow-sm border 
            transition-all duration-200">
          {/* Header */}
          <div className="flex items-center justify-between ml-4 mb-2">
            {/* Left: Title */}
            <h1 className="text-2xl font-bold text-gray-900">
              {panelCopy.panelTitle}
            </h1>

            {/* Right: Download */}
            <div className="flex items-center gap-6">
              {/* Download Button — hidden for TOC v2 (individual sections
                  can be downloaded via Preview). For other orgs (including
                  SLaM Report Builder): disabled when no sections are approved,
                  any CAR section is processing, OR the Patient Report CF chain
                  is running. */}
              {panelCopy.showDownloadButton && (
                <>
                  <button
                    data-tooltip-id="download-report-click"
                    onClick={handleDownloadClick}
                    disabled={!hasApprovedSections || isAnyCARSectionProcessing() || isPatientReportProcessing}
                    className={`px-6 py-2.5 text-sm font-bold rounded-md 
                        focus:outline-none focus:ring-2 focus:ring-violet-500 
                        focus:ring-offset-2 flex items-center justify-center
                        ${hasApprovedSections && !isAnyCARSectionProcessing() && !isPatientReportProcessing
                        ? 'bg-violet-900 text-white cursor-pointer hover:bg-violet-800'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      }`}
                  >
                    <DownloadIcon className="w-5 h-5 mr-2" aria-hidden="true" />
                    {panelCopy.downloadButtonLabel}
                  </button>
                  {(!hasApprovedSections || isAnyCARSectionProcessing() || isPatientReportProcessing) && (
                    <Tooltip
                      id={`download-report-click`}
                      place="top"
                      className="z-50 text-xs font-semibold"
                      style={{
                        maxWidth: '250px',
                        whiteSpace: 'normal',
                        backgroundColor: 'rgba(34, 34, 34, 1)',
                        borderRadius: '15px',
                        color: '#fff',
                      }}
                    >
                      {isPatientReportProcessing
                        ? "Please wait for the Patient Report to finish processing before downloading."
                        : isAnyCARSectionProcessing()
                          ? "Please wait for all documents to finish processing before downloading the report."
                          : "At least one section of the report needs to be generated and approved before you can download the Assessment."}
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Connected Document Sections */}
          <div className="relative">
            {/* Vertical connecting line */}
            <div className="absolute left-[40px] top-12 bottom-12 w-0.5 bg-gray-200"></div>

            {/* Document Sections Stack */}
            <div className="">
              {sections.map((section) => {
                // Skip rendering sections hidden by diagnosis path
                // SSI: hidden for non-ADHD flow unless org is SSI-first (SLaM)
                // PAD: hidden for non-ADHD flow for all orgs
                // Early exit: SSI, DD and PAD are all hidden (CI only)
                if (isSectionHiddenByDiagnosisPath(section.id)) {
                  return null;
                }

                const displayIndex = sectionDisplayNumbers.get(section.id);

                return (
                  <div key={section.id} className="relative">
                    <div
                      className={`
                        w-full flex items-center gap-6 my-1 py-2 px-4 rounded-lg transition-all duration-200
                        ${getBackgroundClass(section.id, section.status, processingStatuses)}
                        ${((section.id === 'ssi' || section.id === 'pad') && !wf.isSSIAlwaysExpected && isAdhdFlow)
                          ? `overflow-hidden transition-all duration-400 ease-in-out ${showADHDTransition ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`
                          : ''}
                        ${((section.id === 'qbCheck' && showQbCheck) || (section.id === 'qbTest' && showQbTest))
                          ? `overflow-hidden transition-all duration-400 ease-in-out ${showQbTransition ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`
                          : ''}
                      `}
                    >
                      {/* Circle with number - updated to use dynamic displayIndex */}
                      <div className={`
                        relative z-10 flex-shrink-0 w-12 h-12 rounded-full border-2 
                        flex items-center justify-center font-semibold text-lg
                        ${getCircleClass(section.id, section.status, processingStatuses)}
                      `}>
                        {displayIndex}
                      </div>

                      {/* Section Info */}
                      <div className="flex-1 flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">{section.reportStage}</h3>
                            {getStatusIcon(section)}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {/* Show simple "Last updated" timestamp for auto-generated / non-reviewed sections
                                (Cover Page, Rating Scales, and TOC QbCheck which is auto-approved with no clinician review).
                                Guard: only show when the timestamp is truthy — serverTimestamp() sentinels
                                resolve to null in the local cache until confirmed by the server. */}
                            {(() => {
                              const autoSectionTimestamp =
                                section.id === 'coverPage' ? MANAGE_REPORTS.cpReportApprovedAt
                                : section.id === 'qbCheck' ? MANAGE_REPORTS.qbCheckReportApprovedAt
                                : section.id === 'attachmentsRatingScales' ? MANAGE_REPORTS.ratingScalesReportApprovedAt
                                : null;
                              return (section.id === 'coverPage' || section.id === 'attachmentsRatingScales' || (section.id === 'qbCheck' && section.isPassthroughMode))
                                && section.status === 'approved'
                                && autoSectionTimestamp;
                            })() ? (
                              <p className="text-sm italic text-gray-700">
                                Last updated on {formatDateTime(
                                  section.id === 'coverPage' 
                                    ? MANAGE_REPORTS.cpReportApprovedAt 
                                    : section.id === 'qbCheck'
                                      ? MANAGE_REPORTS.qbCheckReportApprovedAt
                                      : MANAGE_REPORTS.ratingScalesReportApprovedAt
                                )}
                              </p>
                            ) : (
                              <>
                                <p className="text-sm italic text-gray-700">
                                  {section.status === 'approved'
                                    ? (section.containsAIOutputs
                                      ? `AI outputs last approved`
                                      : `Approved by`)
                                    : section.status === 'ready-for-review'
                                      ? (section.containsAIOutputs
                                        ? 'AI outputs ready for review by'
                                        : 'Ready for review by')
                                      : 'Assigned to'}
                                  {/* Append " on <date> by" when the timestamp has resolved.
                                      Guard: serverTimestamp() sentinels are null in the local cache
                                      until confirmed — omit the date suffix until then to avoid
                                      "on Unknown date by". */}
                                  {(() => {
                                    if (section.status !== 'approved') return '';
                                    const ts =
                                      section.id === 'clinicalInterview' ? (MANAGE_REPORTS.sections?.['1']?.lastReviewedAt || MANAGE_REPORTS.ciReportApprovedAt) :
                                      section.id === 'semiStructuredInterview' ? (MANAGE_REPORTS.sections?.['2']?.lastReviewedAt || MANAGE_REPORTS.ssiReportApprovedAt) :
                                      section.id === 'diagnosticDecision' ? (MANAGE_REPORTS.sections?.['3']?.lastReviewedAt || MANAGE_REPORTS.ddReportApprovedAt) :
                                      section.id === 'postAssessmentDiscussion' ? (MANAGE_REPORTS.sections?.['4']?.lastReviewedAt || MANAGE_REPORTS.padReportApprovedAt) :
                                      section.id === 'qbCheck' ? MANAGE_REPORTS.qbCheckReportApprovedAt :
                                      section.id === 'qbTest' ? MANAGE_REPORTS.qbTestReportApprovedAt :
                                      null;
                                    return ts ? ` on ${formatDateTime(ts)} by` : ' by';
                                  })()}
                                </p>
                                {(section.id === 'attachmentsRatingScales' || (section.id === 'qbCheck' && section.isPassthroughMode)) ? (
                                  <div className="flex flex-wrap items-center gap-2">
                                    {primaryAssignedClinicianBadges.length > 0 ? (
                                      primaryAssignedClinicianBadges.map((c) => (
                                        <div
                                          key={c.id}
                                          title={c.tooltip}
                                          className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help"
                                        >
                                          <span>{c.name}</span>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help">
                                        <span>{section.reviewer.name}</span>
                                      </div>
                                    )}
                                  </div>
                                ) : section.id === 'diagnosticDecision' ? (
                                  <div className="flex flex-wrap items-center gap-2">
                                    {diagnosticDecisionClinicianBadges.length > 0 ? (
                                      diagnosticDecisionClinicianBadges.map((c) => (
                                        <div
                                          key={c.id}
                                          title={c.tooltip}
                                          className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help"
                                        >
                                          <span>{c.name}</span>
                                        </div>
                                      ))
                                    ) : (
                                      <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help">
                                        <span>{section.reviewer.name}</span>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div className="inline-flex border items-center gap-2 px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700 transition-colors duration-200 cursor-help">
                                    <span>{section.reviewer.name}</span>
                                  </div>
                                )}
                              </>
                            )}
                          </div>

                        </div>

                        {/* Action */}
                        <div className="flex items-center gap-3">
                          {/* Show explanatory text for locked sections */}
                          {section.status === 'locked' && getLockedSectionText(section.id) && (
                            <div className="text-xs text-gray-500 italic">
                              {getLockedSectionText(section.id)}
                            </div>
                          )}

                          {/* SSI needs-redo status (3 Borough / SLaM workflow) */}
                          {section.status === 'needs-redo' && section.id === 'semiStructuredInterview' && (
                            <div className="flex items-center gap-3">
                              <div className="text-xs text-amber-700 italic max-w-xs">
                                {getSSINeedsRedoMessage()}
                              </div>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/patient/${patientId}/proforma/semi-structured-interview/symptoms`);
                                }}
                                className="flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 bg-amber-600 text-white hover:bg-amber-700"
                              >
                                Go to module
                              </button>
                            </div>
                          )}

                          {/* PAD staleness cue (SLaM v2). Unlike the SSI reset, this does
                              not hijack the report status — the PAD report may still be
                              approved — so it renders alongside the normal review action as
                              a two-state advisory + redo CTA. */}
                          {section.id === 'postAssessmentDiscussion' && showPadStalenessCue && (
                            <div className="flex items-center gap-3 whitespace-nowrap">
                              {/* The advisory ("why is this here?") now lives on the
                                  Redo button as a hover tooltip rather than as a long
                                  wrapping sentence, so the row stays compact. The amber
                                  vs grey button still signals alert vs acknowledged. */}
                              <button
                                data-tooltip-id="pad-staleness-tip"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowPadRedoDialog(true);
                                }}
                                className={`flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200 ${
                                  padMismatchAcknowledged
                                    ? 'text-gray-900 border border-gray-300 hover:bg-gray-100'
                                    : 'bg-amber-600 text-white hover:bg-amber-700'
                                }`}
                              >
                                Redo
                              </button>
                              <Tooltip
                                id="pad-staleness-tip"
                                place="top"
                                className="z-50 text-xs font-semibold"
                                style={{
                                  maxWidth: '250px',
                                  whiteSpace: 'normal',
                                  backgroundColor: 'rgba(34, 34, 34, 1)',
                                  borderRadius: '15px',
                                  color: '#fff',
                                }}
                              >
                                {padMismatchAcknowledged
                                  ? 'This report reflects a previous outcome (acknowledged). Redo to align it with the current diagnosis.'
                                  : 'The diagnosis changed after this discussion was completed. Redo to align it with the current diagnosis.'}
                              </Tooltip>
                            </div>
                          )}

                          {section.status !== 'locked' && section.status !== 'needs-redo' && section.containsAIOutputs &&
                            section.reviewer.id === auth.currentUser?.uid && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (section.route) {
                                    navigate(`/patient/${patientId}/${section.route}`);
                                  }
                                }}
                                className={`
                              flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200
                              ${section.status === 'ready-for-review'
                                    ? 'bg-violet-800 text-white hover:bg-violet-700'
                                    : 'text-gray-900 border border-gray-300 hover:bg-green-200'
                                  }
                            `}
                              >
                                {section.status === 'ready-for-review' ? 'Review AI Outputs' : 'Review Paragraphs'}
                              </button>
                            )}

                          {/* Regenerate CI outputs button.
                              Visible when the clinician needs to re-run AI paragraph generation
                              with updated inputs - either because the org's workflow allows ongoing
                              form edits (org-level exemption) or because a specific patient's forms
                              were unlocked post-assessment (patient-level formsUnlocked override). */}
                          {section.id === 'clinicalInterview' && section.status !== 'locked' &&
                            section.reviewer.id === auth.currentUser?.uid &&
                            (claims?.organisation === 'Psychological Consultations' || patientDataModel?.formsUnlocked === true) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowCIRegenerateDialog(true);
                                }}
                                disabled={processingStatuses.clinicalInterview === 'processing'}
                                className={`
                                  flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200
                                  ${processingStatuses.clinicalInterview === 'processing'
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'text-gray-900 border border-gray-300 hover:bg-red-600 hover:text-white'}
                                `}
                              >
                                Regenerate AI outputs
                              </button>
                            )}

                          {/* Regenerate SSI outputs button.
                              Same gating logic as the CI regenerate button - shown when the
                              patient's forms were unlocked so that updated rating scale data
                              (e.g. BAARS-IV) can be incorporated into re-generated SSI paragraphs. */}
                          {section.id === 'semiStructuredInterview' && section.status !== 'locked' &&
                            section.reviewer.id === auth.currentUser?.uid &&
                            patientDataModel?.formsUnlocked === true && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowSSIRegenerateDialog(true);
                                }}
                                disabled={processingStatuses.semiStructuredInterview === 'processing'}
                                className={`
                                  flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200
                                  ${processingStatuses.semiStructuredInterview === 'processing'
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'text-gray-900 border border-gray-300 hover:bg-red-600 hover:text-white'}
                                `}
                              >
                                Regenerate AI outputs
                              </button>
                            )}

                          {/* QbCheck section handling
                              Only the assigned clinician (Lead Clinician / SSI reviewer) can access the
                              QbCheck form. TOC's simplified flow and other orgs' full form both require
                              the assigned clinician — Support / Org Admin users cannot submit QbCheck. */}
                          {section.id === 'qbCheck' && processingStatuses.qbCheck !== 'processing' && section.reviewer.id === auth.currentUser?.uid && (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const formPath = section.isPassthroughMode
                                    ? `/patient/${patientId}/documents/qb-check-form-toc`
                                    : `/patient/${patientId}/documents/qb-check-form`;
                                  navigate(formPath);
                                }}
                                className={`
                                    text-gray-900 border border-gray-300 hover:bg-gray-100 
                                    flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200
                                    ${section.status === 'locked' ? 'bg-violet-800 text-white hover:bg-violet-700' : ''}
                                  `}
                              >
                                {section.status === 'locked' ? 'Add QbCheck Results' : 'Re-add QbCheck Results'}
                              </button>
                            </>
                          )}

                          {/* Rating Scales section handling */}
                          {section.id === 'attachmentsRatingScales' && canGenerateRatingScales && (
                            <>
                              {/* Only show "Generate Section" when not processing and either locked IF there are available scales or not locked */}
                              {processingStatuses.ratingScales !== 'processing' && (
                                (section.status !== 'locked' || hasAvailableScales) ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowRatingScalesWizard(true);
                                    }}
                                    className={`
                                  text-gray-900 border border-gray-300 hover:bg-gray-100 
                                  flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200
                                  ${section.status === 'locked' ? 'bg-violet-800 text-white hover:bg-violet-700' : ''}
                                `}
                                  >
                                    {section.status === 'locked' ? 'Generate Section' : 'Re-generate Section'}
                                  </button>
                                ) : (
                                  <div className="text-xs text-gray-500 italic">
                                    Available after at least one self-rated scale is completed
                                  </div>
                                )
                              )}
                              
                              {/* Error retry button */}
                              {processingStatuses.ratingScales === 'error' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowRatingScalesWizard(true);
                                  }}
                                  className="bg-white text-primary border border-primary hover:bg-primary/5 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                                >
                                  Try again
                                </button>
                              )}
                            </>
                          )}

                          {/* Error retry button for Cover Page */}
                          {section.id === 'coverPage' && 
                           processingStatuses.coverPage === 'error' &&
                           section.reviewer.id === auth.currentUser?.uid && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                // Retry cover page generation
                                TriggerCoverPage({
                                  clinicianId: auth.currentUser?.uid,
                                  patientId: patientId,
                                  reportType: isAdhdAssessmentFlow(patientDataModel, wf) ? "Cover Page - Regular" : "Cover Page - Not ADHD - CI"
                                });
                              }}
                              className="bg-white text-primary border border-primary hover:bg-primary/5 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                            >
                              Try again
                            </button>
                          )}

                          {/* Error retry button for AI sections (CI, SSI, DD, PAD):
                              re-renders the approved content to PDF. Only shown
                              when the section's processing status is 'error', the
                              section has AI outputs, and the current user is the
                              assigned reviewer. */}
                          {section.containsAIOutputs &&
                           processingStatuses[section.id] === 'error' &&
                           section.reviewer.id === auth.currentUser?.uid && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                TriggerRenderDocTemplate({
                                  reportStage: section.reportStage,
                                  clinicianId: auth.currentUser?.uid,
                                  patientId: patientId,
                                });
                              }}
                              className="bg-white text-primary border border-primary hover:bg-primary/5 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                            >
                              Try again
                            </button>
                          )}

                          {/* View button for non-AI sections that are not locked (and not QbCheck or QbTest or RatingScales) */}
                          {section.route && !section.containsAIOutputs && section.status !== 'locked' &&
                            section.id !== 'qbCheck' && section.id !== 'qbTest' &&
                            section.id !== 'attachmentsRatingScales' && section.reviewer.id === auth.currentUser?.uid && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (section.route) {
                                    navigate(`/patient/${patientId}/${section.route}`);
                                  }
                                }}
                                className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                              >
                                View
                              </button>
                            )}

                          {/* Preview button — opens the rendered PDF in an
                              embedded dialog. Visible to all users (read-only)
                              when the section has been generated (approved OR
                              ready-for-review) and is not currently processing.
                              This lets clinicians view the rendered document
                              before approving, matching the CypAdhd dashboard
                              pattern where Preview depends on document existence
                              rather than approval status. Only shown for sections
                              whose Firestore document key is compatible with the
                              embed CF (excludes Cover Page and Rating Scales). */}
                          {section.embedReportStage
                          && (section.status === 'approved' || section.status === 'ready-for-review')
                          &&
                            processingStatuses[
                              section.id === 'attachmentsRatingScales' ? 'ratingScales'
                              : section.id === 'coverPage' ? 'coverPage'
                              : section.id
                            ] !== 'processing' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const docKey = SECTION_DOCUMENT_KEY[section.id];
                                  const lastGen = docKey
                                    ? patientDataModel?.documents?.[docKey]?.lastGenerated || null
                                    : null;
                                  handleOpenPreview(section.embedReportStage, lastGen);
                                }}
                                className="text-gray-900 border border-gray-300 hover:bg-gray-100 flex items-center justify-center py-2 px-4 text-sm font-semibold min-w-24 rounded-md transition-all duration-200"
                              >
                                <SearchIcon className="w-4 h-4 mr-1.5" />
                                Preview
                              </button>
                            )}
                        </div>

                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        
        {/* Additional Documents Panel (includes the Semble MDT Report row
            for Well Life Clinic) */}

        {MemoizedAdditionalDocuments}

      </div>
      <ReportBundleDialog
        open={showBundleDialog}
        onOpenChange={setShowBundleDialog}
        patientId={patientId}
        patientDataModel={patientDataModel}
        visibleSections={visibleSectionsForDialog}
        processingStatuses={processingStatuses}
        title={panelCopy.bundleDialogTitle}
      />

      {/* Regenerate CI confirmation dialog.
          Shown for orgs whose workflow allows ongoing form edits (e.g. Psychological
          Consultations) or for individual patients whose forms were unlocked post-assessment
          via the formsUnlocked Firestore flag. */}
      {(claims?.organisation === 'Psychological Consultations' || patientDataModel?.formsUnlocked === true) && (
      <AlertDialog.Root open={showCIRegenerateDialog} onOpenChange={(open) => { if (!isCIRegenerating) setShowCIRegenerateDialog(open); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000]" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto z-[1001]">
            <div className="mb-4">
              <AlertDialog.Title className="text-xl font-bold">Regenerate AI outputs (Clinical Interview)</AlertDialog.Title>
              <AlertDialog.Description>
                <div className="mt-3 text-sm text-gray-700 space-y-3">
                  <p>
                    This will re-generate every AI paragraph for the Clinical Interview report section.
                    Please review what this means before proceeding:
                  </p>
                  <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                      New paragraphs will be generated using the <span className="font-semibold">latest available inputs</span>, including
                      any recent changes or additions the patient has made to their questionnaires, as well as
                      your existing clinician notes and decisions.
                    </li>
                    <li>
                      The new paragraphs <span className="font-semibold">will replace the existing ones</span> -
                      including any text you have previously edited or approved.
                    </li>
                    <li>
                      After regeneration, <span className="font-semibold">these paragraphs will need to be re-reviewed and re-approved</span> before
                      the report section can be finalised.
                    </li>
                    <li>
                      The process may take <span className="font-semibold">up to a few minutes</span>.
                    </li>
                  </ul>
                  <p className="font-semibold text-red-700">
                    This action cannot be undone.
                  </p>
                </div>
              </AlertDialog.Description>
            </div>
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  className={`py-2 px-4 mb-2 font-semibold border border-gray-300 rounded-md ${isCIRegenerating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                  disabled={isCIRegenerating}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              
                <button
                  className={`flex items-center px-4 py-2 mb-2 font-semibold rounded-md ${isCIRegenerating ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'}`}
                  onClick={async () => {
                    if (isCIRegenerating) return;
                    setIsCIRegenerating(true);
                    try {
                      const organisationName = claims?.organisation || 'default';
                      const { sections: ciSections, excludedKeys } = buildCIExclusionsContext(patientDataModel, organisationName);
                      const visibleCITitles = getVisibleCISections(ciSections, excludedKeys).map(s => s.sectionTitle);
                      // The "Assessment Conclusion" paragraph (one-clinician
                      // "End assessment" after the CI) has no numbered CI
                      // section, so it is not in visibleCITitles. Include it
                      // while the assessment is ended so "Regenerate all"
                      // really regenerates the whole CI report; the backend
                      // gate makes this a safe no-op in every other state.
                      const regenerateTitles = wf.assessmentEndedAfterCI
                        ? [...visibleCITitles, ASSESSMENT_CONCLUSION_SECTION_TITLE]
                        : visibleCITitles;

                      await TriggerRegenerateCIStage({
                        patientId,
                        clinicianId: auth.currentUser?.uid,
                        sectionTitles: regenerateTitles,
                      });
                      await delay(1000);
                      setShowCIRegenerateDialog(false);
                    } catch (e) {
                      console.error('Regeneration failed', e);
                    } finally {
                      setIsCIRegenerating(false);
                    }
                  }}
                  disabled={isCIRegenerating}
                >
                  {isCIRegenerating && (
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isCIRegenerating ? 'Regenerating…' : 'Re-generate All Paragraphs'}
                </button>
              
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
      )}

      {/* Regenerate SSI confirmation dialog.
          Only relevant for patients whose forms were unlocked post-assessment, where
          updated rating scale data (e.g. BAARS-IV) needs to feed into fresh SSI paragraphs. */}
      {patientDataModel?.formsUnlocked === true && (
      <AlertDialog.Root open={showSSIRegenerateDialog} onOpenChange={(open) => { if (!isSSIRegenerating) setShowSSIRegenerateDialog(open); }}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/50 backdrop-blur-sm z-[1000]" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-6 w-[600px] max-w-[90vw] max-h-[85vh] overflow-y-auto z-[1001]">
            <div className="mb-4">
              <AlertDialog.Title className="text-xl font-bold">Regenerate AI outputs (Semi-Structured Interview)</AlertDialog.Title>
              <AlertDialog.Description>
                <div className="mt-3 text-sm text-gray-700 space-y-3">
                  <p>
                    This will re-generate every AI paragraph for the Semi-Structured Interview report section.
                    Please review what this means before proceeding:
                  </p>
                  <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                      New paragraphs will be generated using the <span className="font-semibold">latest available inputs</span>, including
                      any recent changes or additions the patient has made to their questionnaires and rating scales, as well as your existing clinician notes and decisions.
                    </li>
                    <li>
                      The new paragraphs <span className="font-semibold">will replace the existing ones</span> -
                      including any text you have previously edited or approved.
                    </li>
                    <li>
                      After regeneration, <span className="font-semibold">these paragraphs will need to be re-reviewed and re-approved</span> before
                      the report section can be finalised.
                    </li>
                    <li>
                      The process may take <span className="font-semibold">up to a few minutes</span>.
                    </li>
                  </ul>
                  <p className="font-semibold text-red-700">
                    This action cannot be undone.
                  </p>
                </div>
              </AlertDialog.Description>
            </div>
            <div className="flex justify-end gap-3">
              <AlertDialog.Cancel asChild>
                <button
                  className={`py-2 px-4 mb-2 font-semibold border border-gray-300 rounded-md ${isSSIRegenerating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}`}
                  disabled={isSSIRegenerating}
                >
                  Cancel
                </button>
              </AlertDialog.Cancel>
              
                <button
                  className={`flex items-center px-4 py-2 mb-2 font-semibold rounded-md ${isSSIRegenerating ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-red-600 text-white hover:bg-red-700'}`}
                  onClick={async () => {
                    if (isSSIRegenerating) return;
                    setIsSSIRegenerating(true);
                    try {
                      // SSI has two generatable sections: Symptoms and Domains of Functional Impairment.
                      // "Recommended Diagnosis" is a clinician-only decision section with no AI outputs.
                      const ssiSectionTitles = ['Symptoms', 'Domains of Functional Impairment'];

                      await TriggerRegenerateSSIStage({
                        patientId,
                        clinicianId: auth.currentUser?.uid,
                        sectionTitles: ssiSectionTitles,
                      });
                      await delay(1000);
                      setShowSSIRegenerateDialog(false);
                    } catch (e) {
                      console.error('SSI regeneration failed', e);
                    } finally {
                      setIsSSIRegenerating(false);
                    }
                  }}
                  disabled={isSSIRegenerating}
                >
                  {isSSIRegenerating && (
                    <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {isSSIRegenerating ? 'Regenerating…' : 'Re-generate All Paragraphs'}
                </button>
              
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
      )}

      <RatingScalesWizard
        open={showRatingScalesWizard}
        onOpenChange={setShowRatingScalesWizard}
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
      />

      <DocumentEmbedDialog
        isOpen={isPreviewDialogOpen}
        onClose={handleClosePreview}
        reportStage={selectedPreviewReport}
        patientId={patientId}
        clinicianId={auth.currentUser?.uid}
        lastGenerated={selectedPreviewLastGenerated}
      />

      {/* SLaM PAD redo — opened from the PAD row's staleness cue. The clinician
          has already chosen to redo, so it opens straight on the redo details
          step (new appointment date) and, on success, sends them to PAD Review. */}
      <PadRedoDialog
        isOpen={showPadRedoDialog}
        onClose={() => setShowPadRedoDialog(false)}
        patientId={patientId}
        patientDataModel={patientDataModel}
        mode="redo"
        onBeforeRedo={flushPendingUpdates}
        onAfterRedo={async () => {
          await fetchPatientData(patientId, true);
          setShowPadRedoDialog(false);
          navigate(`/patient/${patientId}/proforma/post-assessment-discussion/review-assessment`);
        }}
      />

    </Layout>
  );
}